import { useTranslations } from "use-intl";
import { Button, ButtonBase } from "@mui/material";
import { useFormContext, useWatch } from "react-hook-form";
import type { BookingData } from "../../types";
import type { TripDetails } from "@/app/lib/types/trip";
import { useState } from "react";
import { TripTourDrawer } from "./TripTourDrawer";
import { PlusIcon } from "@phosphor-icons/react";
import { formatCurrency } from "@/app/lib/utils/currency";
import { rudderAnalytics, TripTrackingEvents } from "@/app/lib/rudderstack";
import { IconBalloon } from "@/app/icons";

type TripTourCardProps = {
  trip: TripDetails;
  source: "summary" | "details";
};

export function TripTourCard({ trip, source }: TripTourCardProps) {
  const t = useTranslations();
  const { setValue } = useFormContext<BookingData>();

  const [isTourDetailsOpen, setTourDetailsOpen] = useState(false);

  const isTripTourAdded = useWatch<BookingData, "isTripTourAdded">({
    name: "isTripTourAdded",
  });

  return (
    <>
      <div>
        <ButtonBase
          type="button"
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            borderRadius: "24px",
            backgroundColor: "white",
            p: "16px",
          }}
          onClick={() => {
            rudderAnalytics.track(
              TripTrackingEvents.USER_VIEWED_TRIP_OPTIONAL_TOURS,
              {
                source,
                trip_id: trip.id,
                trip_name: trip.name,
                price: trip.tourPrice ?? 0,
              },
            );

            setTourDetailsOpen(true);
          }}
        >
          <div className="flex items-center gap-2">
            <IconBalloon />

            <div className="text-left">
              <p className="font-medium text-text-primary">
                {t("trips.tours.card.title")}
              </p>

              <p className="text-sm font-medium text-core-rahal-600">
                {isTripTourAdded
                  ? t("trips.tours.card.included")
                  : t("trips.tours.card.priceFrom", {
                      price: formatCurrency(trip.tourPrice),
                    })}
              </p>
            </div>
          </div>

          <Button
            component="span"
            variant="text"
            startIcon={!isTripTourAdded && <PlusIcon />}
            sx={{ p: 0 }}
            size="small"
          >
            {t(`trips.tours.card.${isTripTourAdded ? "details" : "add"}`)}
          </Button>
        </ButtonBase>
      </div>

      <TripTourDrawer
        trip={trip}
        open={isTourDetailsOpen}
        onClose={() => setTourDetailsOpen(false)}
      >
        {isTripTourAdded ? (
          <Button
            fullWidth
            size="large"
            variant="outlined"
            color="error"
            onClick={() => {
              rudderAnalytics.track(
                TripTrackingEvents.USER_REMOVED_TRIP_OPTIONAL_TOURS,
                {
                  source,
                  trip_id: trip.id,
                  trip_name: trip.name,
                  price: trip.tourPrice ?? 0,
                },
              );

              setValue("isTripTourAdded", false);
              setTourDetailsOpen(false);
            }}
          >
            {t("trips.tours.drawer.remove")}
          </Button>
        ) : (
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={() => {
              rudderAnalytics.track(
                TripTrackingEvents.USER_ADDED_TRIP_OPTIONAL_TOURS,
                {
                  source,
                  trip_id: trip.id,
                  trip_name: trip.name,
                  price: trip.tourPrice ?? 0,
                },
              );

              setValue("isTripTourAdded", true);
              setTourDetailsOpen(false);
            }}
          >
            {t("trips.tours.drawer.addTours")}
          </Button>
        )}

        {isTripTourAdded && (
          <Button
            fullWidth
            size="large"
            variant="contained"
            onClick={() => {
              setTourDetailsOpen(false);
            }}
          >
            {t("trips.tours.drawer.confirm")}
          </Button>
        )}
      </TripTourDrawer>
    </>
  );
}
